import { Type } from "class-transformer";
import { ArrayMinSize, IsArray, IsBoolean, IsEmail, IsNotEmpty, IsNumber, IsObject, IsOptional, IsUrl, ValidateNested } from "class-validator";
import { IBase, ISchedule, IShop, IShopProductionCapacity, ISocialMediaLinks, OrderMode } from "../interfaces";
import { LocationPointDTO } from "./utils.dto";
import { ScheduleDTO } from "./catalog";

export class ShopDTO implements Omit<IShop, keyof IBase | 'deliveryZones' | 'slug' | 'printers'> {
  @IsNotEmpty({ message: 'Le nom interne est requis' })
  internalName: string;

  @IsNotEmpty({ message: 'Le numéro de siret est requis' })
  siret: string;

  @IsNotEmpty({ message: 'Le nom publique est requis' })
  publicName: string;

  @IsNotEmpty({ message: 'La description est requise' })
  description: string;

  @IsNotEmpty({ message: 'L\'adresse est requise' })
  address: string;

  @IsOptional()
  zipCode: string;

  @IsOptional()
  city: string;

  @IsOptional()
  naf: string;

  @IsOptional()
  vatNumber: string;

  @IsNotEmpty({ message: 'Le numéro de téléphone est requis' })
  phone: string;

  @IsOptional()
  contractVAD: string;

  @IsNotEmpty({ message: 'L\'email est requis' })
  @IsEmail(undefined, { message: 'Le format de l\'email est incorrect' })
  email: string;

  @IsOptional({ message: 'Le site internet est requis' })
  @IsUrl({}, { message: 'Le lien du site internet est incorrect' })
  website: string;

  @IsOptional()
  disabled: boolean;

  @IsNotEmpty({ message: 'Les coordonées GPS sont requises' })
  @Type(() => LocationPointDTO)
  @ValidateNested()
  location: {
    coordinates: number[];
    type: 'Point';
  };

  @IsOptional()
  @Type(() => ScheduleDTO)
  @ValidateNested()
  takeAwaySchedule: ISchedule;

  @IsOptional()
  @Type(() => ScheduleDTO)
  @ValidateNested()
  deliverySchedule: ISchedule;

  @IsOptional()
  @Type(() => ScheduleDTO)
  @ValidateNested()
  platformsSchedule: ISchedule;

  @IsOptional()
  takeAwayTime: number;

  @IsOptional()
  deliveryTime: number;

  @IsOptional()
  defaultTakeAwayTime: number;

  @IsOptional()
  defaultDeliveryTime: number;

  @IsOptional()
  eshopMessage: string;

  @IsOptional()
  @IsBoolean({ message: 'La livraison doit être active ou non' })
  isDeliveryOpen: boolean;

  @IsOptional()
  @IsBoolean({ message: 'La livraison doit être active ou non' })
  isTakeawayOpen: boolean;

  @IsOptional()
  @ValidateNested()
  socialMediaLinks?: ISocialMediaLinks;

  @IsOptional()
  @Type(() => ProductionCapacityDayDTO)
  @ValidateNested({ each: true })
  productionCapacities?: ProductionCapacityDayDTO[];
}


export class ProductionCapacitySlotDTO {
  @IsNumber()
  start: number;

  @IsNumber()
  end: number;

  @IsOptional()
  @IsNumber()
  maxOrders: number;

  @IsOptional()
  @IsObject()
  maxProducts?: Record<string, number | null>;
}


class ProductionCapacityDayDTO implements IShopProductionCapacity {
  @IsNotEmpty({ message: "Ce champ est requis" })
  name: string;

  @IsNotEmpty({ message: "Ce champ est requis" })
  @IsBoolean({ message: "Ce champ est requis" })
  disabled: boolean;

  @IsNotEmpty({ message: "Ce champ est requis" })
  @IsArray()
  @ArrayMinSize(1, { message: "Au moins un mode de commande est requis" })
  orderModes: OrderMode[];

  @IsOptional()
  @IsArray()
  tagsIds?: string[];

  @IsNotEmpty({ message: "Ce champ est requis" })
  @Type(() => ProductionCapacityDTO)
  capacity: ProductionCapacityDTO;
}


export class ProductionCapacityDTO   {
  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ProductionCapacitySlotDTO)
  monday: ProductionCapacitySlotDTO[];

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ProductionCapacitySlotDTO)
  tuesday: ProductionCapacitySlotDTO[];

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ProductionCapacitySlotDTO)
  wednesday: ProductionCapacitySlotDTO[];

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ProductionCapacitySlotDTO)
  thursday: ProductionCapacitySlotDTO[];

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ProductionCapacitySlotDTO)
  friday: ProductionCapacitySlotDTO[];

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ProductionCapacitySlotDTO)
  saturday: ProductionCapacitySlotDTO[];

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ProductionCapacitySlotDTO)
  sunday: ProductionCapacitySlotDTO[];
}